import { Avatar, Box, Button, Collapse, Dialog, IconButton, Menu, MenuItem, Select, Slider, Switch, Typography, useTheme } from '@mui/material'
import { ArrowDropDown, BlurCircular, BlurOn, Circle, DoNotDisturb, Mic, StopCircle, Videocam, VolumeUp } from '@mui/icons-material'
import VirtualBackgroundExtension from "agora-extension-virtual-background"
import React, { useEffect, useRef, useState } from 'react'
import AgoraRTC from 'agora-rtc-sdk-ng'
import { observer } from 'mobx-react'

import { useThemeContext } from '../../contexts/theme.context'
import AudioVisualizer from './AudioVisualizers/AudioVisualizer'
import sampleAudio from '../../assets/audios/SampleAudio.mp3'
import { useMeetingStore } from '../../store/meeting.store'
import { Icons } from '../../utils/utilities'
import VideoPreview from './VideoPreview'
import DataLoadingDots from '../common/DataLoadingDots'

const shortcutsBySections = [
    {
        name: "General",
        keyBindings: [
            { name: "Toggle Fullscreen", shortCuts: [["Ctrl", "F"]] },
            { name: "Open Settings", shortCuts: [["Ctrl", "/"]] },
        ]
    },
    {
        name: "Session Actions",
        keyBindings: [
            { name: "Toggle Mic", shortCuts: [["Alt", "D"]] },
            { name: "Toggle Camera", shortCuts: [["Alt", "E"]] },
            { name: "Raise or Lower Hand", shortCuts: [["Alt", "H"]] },
            { name: "View or Hide Chats", shortCuts: [["Alt", "Ctrl", "C"]] },
            { name: "View or Hide Participants", shortCuts: [["Alt", "Ctrl", "P"]] },
            { name: "Leave", shortCuts: [["Ctrl", "L"]] },
            { name: "Toggle Side drawer", shortCuts: [["Ctrl", "B"]] },
        ]
    }
]

const solidColors = ["rgba(35,35,35,1)", "rgba(230,230,230,1)", "cyan", "orange"]

const layoutMode = [
    { value: "balanced", label: "Balanced" },
    { value: "focused", label: "Focused" },
]

const MeetingSettings = observer( ( { container, open, setOpen, activeTab = "general" } ) => {

    const [changingAudioDevice, setChangingAudioDevice] = useState( false )
    const [activeSettingsTab, setActiveSettingsTab] = useState( activeTab || "general" )
    const [playingSampleAudio, setPlayingSampleAudio] = useState( false )
    const [speakersAnchorEl, setSpeakersAnchorEl] = useState( null )
    const [micsAnchorEl, setMicsAnchorEl] = useState( null )
    const [camsAnchorEl, setCamsAnchorEl] = useState( null )


    const { themeOptions, setSelectedTheme, selectedTheme } = useThemeContext()


    const MeetingStore = useMeetingStore()

    const { palette, border } = useTheme()

    const samAudioElement = useRef()

    const handleThemeChange = e => {
        setSelectedTheme( e.target.value )
        localStorage.setItem( 'preferedTheme', e.target.value )
    }

    const handleLayoutModeChange = event => {
        const { layout = {} } = MeetingStore.getMeetingState
        MeetingStore.setMeetingState( { layout: { ...layout, layoutMode: event.target.value } } )
        localStorage.setItem( 'preferedMeetingLayoutMode', event.target.value )
    }

    const togglePlaySampleAudio = async () => {
        try {
            if ( samAudioElement.current ) {
                if ( samAudioElement.current.paused ) {
                    samAudioElement.current.currentTime = 0
                    await samAudioElement.current.play()
                } else
                    await samAudioElement.current.pause()
            }
        } catch ( err ) {
            console.log( err )
        }
    }

    const handleCameraChange = value => {
        MeetingStore.setMeetingState( { cameraId: value } )
        if ( MeetingStore.getMeetingState.isVideoOn ) {
            const videoTrack = MeetingStore.getLocalVideoTrack
            if ( videoTrack )
                videoTrack.setDevice( MeetingStore.getMeetingState.cameraId )
        }
        setCamsAnchorEl( null )
    }

    const handleMicChange = async value => {
        MeetingStore.setMeetingState( { micId: value } )
        if ( MeetingStore.getMeetingState.isAudioOn ) {
            const audioTrack = MeetingStore.getLocalAudioTrack
            if ( audioTrack ) {
                setChangingAudioDevice( true )
                await audioTrack.setDevice( value )
                setChangingAudioDevice( false )
            }
        }
        setMicsAnchorEl( null )
    }

    const handleSpeakerChange = async value => {
        MeetingStore.setMeetingState( { speakerId: value } )

        const audioElem = samAudioElement.current

        if ( audioElem && typeof audioElem.setSinkId !== "undefined" ) {
            try {
                await audioElem.setSinkId( value )
            } catch ( err ) {
                console.error( "Error setting output device:", err )
            }
        } else {
            console.warn( "setSinkId not supported in this browser" )
        }
        setSpeakersAnchorEl( null )
    }


    return (
        <Dialog container={container.current} onClose={() => setOpen( false )} slotProps={{ paper: { sx: { height: "60%", width: "90%", maxWidth: "sm", borderRadius: "10px", display: "flex", flexDirection: "column" } } }} disableAutoFocus disableEnforceFocus open={open}>


            <Box id="sampleSoundTest" onPlay={() => setPlayingSampleAudio( true )} onPause={() => setPlayingSampleAudio( false )} loop component="audio" src={sampleAudio} ref={samAudioElement} />

            <Box display="flex" justifyContent="space-between" gap="20px" alignItems="center" borderBottom={border[1]} padding="10px 20px">
                <Typography component="h2">Settings And Configurations</Typography>
                <IconButton onClick={() => setOpen( false )} size='small'>
                    {Icons.small.CloseIcon}
                </IconButton>
            </Box>
            <Box overflow="auto" flexGrow={1} display="flex" flexDirection={{ sm: "row", xs: "column" }} >
                <Box height={{ xs: "auto", sm: "100%" }} borderBottom={{ xs: border[1], sm: "none" }} borderRight={{ sm: border[1], xs: "none" }} padding={{ sm: "20px", xs: "10px 20px" }} gap="5px" display="flex" overflow="auto" flexDirection={{ sm: "column", xs: "row" }} className="slim-custom-scrollbar">
                    <Box bgcolor={activeSettingsTab === "general" && palette.rowHover} padding="5px 10px" onClick={() => setActiveSettingsTab( "general" )} borderRadius="5px" sx={{ cursor: "pointer", ":hover": { bgcolor: palette.rowHover } }} >
                        <Typography variant='subtitle2' component="h3">General</Typography>
                    </Box>
                    <Box bgcolor={activeSettingsTab === "audio" && palette.rowHover} padding="5px 10px" onClick={() => setActiveSettingsTab( "audio" )} borderRadius="5px" sx={{ cursor: "pointer", ":hover": { bgcolor: palette.rowHover } }} >
                        <Typography variant='subtitle2' component="h3">Audio</Typography>
                    </Box>
                    <Box bgcolor={activeSettingsTab === "video" && palette.rowHover} padding="5px 10px" onClick={() => setActiveSettingsTab( "video" )} borderRadius="5px" sx={{ cursor: "pointer", ":hover": { bgcolor: palette.rowHover } }} >
                        <Typography variant='subtitle2' component="h3">Video</Typography>
                    </Box>
                    <Box bgcolor={activeSettingsTab === "shortcuts" && palette.rowHover} padding="5px 10px" onClick={() => setActiveSettingsTab( "shortcuts" )} borderRadius="5px" sx={{ cursor: "pointer", ":hover": { bgcolor: palette.rowHover } }} >
                        <Typography variant='subtitle2' component="h3">Shortcuts</Typography>
                    </Box>
                </Box>
                <Box overflow="auto" flexGrow={1}>
                    {activeSettingsTab === "general" && <Box paddingY="10px">
                        <Box display="flex" padding="10px" justifyContent="space-between" gap="20px" alignItems="flex-start">
                            <Typography variant='subtitle2' component="h3">Appearance</Typography>
                            <Box>
                                <Select MenuProps={{ container: container.current, slotProps: { paper: { sx: { bgcolor: palette.contentBg } } } }} sx={{ height: "25px", textTransform: "capitalize", fontSize: "12px" }} size="small" value={selectedTheme} onChange={handleThemeChange} >
                                    {themeOptions.map( theme => (
                                        <MenuItem key={theme} value={theme} dense sx={{ minHeight: "initial", padding: "5px 10px", fontSize: "12px", textTransform: "capitalize" }}>{theme}</MenuItem>
                                    ) )}
                                </Select>
                            </Box>
                        </Box>
                        <Box display="flex" padding="10px" justifyContent="space-between" gap="20px" alignItems="flex-start">
                            <Typography variant='subtitle2' component="h3">Layout Mode</Typography>
                            <Box>
                                <Select MenuProps={{ container: container.current, slotProps: { paper: { sx: { bgcolor: palette.contentBg } } } }} sx={{ height: "25px", textTransform: "capitalize", fontSize: "12px" }} size="small" value={MeetingStore.getMeetingState?.layout?.layoutMode || "focused"} onChange={handleLayoutModeChange} >
                                    {layoutMode.map( mode => (
                                        <MenuItem key={mode.value} value={mode.value} dense sx={{ minHeight: "initial", padding: "5px 10px", fontSize: "12px", textTransform: "capitalize" }}>{mode.label}</MenuItem>
                                    ) )}
                                </Select>
                            </Box>
                        </Box>
                    </Box>}

                    {activeSettingsTab === "shortcuts" && <Box overflow="auto" paddingY="10px">
                        {shortcutsBySections.map( ( section, index ) => (
                            <Box borderBottom={index !== shortcutsBySections.length - 1 ? border[1] : "none"} padding="10px" key={section.name}>
                                <Typography variant='subtitle2' paddingX="10px" display="block" fontSize="12px" color='GrayText'>{section.name}</Typography>
                                {section.keyBindings.map( keyBinding => (
                                    <Box key={keyBinding.name} display="flex" padding="10px" justifyContent="space-between" gap="20px" alignItems="flex-start">
                                        <Typography variant='subtitle2' component="h3">{keyBinding.name}</Typography>
                                        <Box display="flex" justifyContent="flex-end" gap="5px" flexWrap="wrap">
                                            {keyBinding.shortCuts.map( ( shortcut, index ) => (
                                                <Box sx={{ cursor: "pointer", bgcolor: palette.contentBg }} fontSize="12px" key={index} padding="5px" border={border[1]} borderRadius="5px" >
                                                    {shortcut?.join( " + " )}
                                                </Box>
                                            ) )}
                                        </Box>
                                    </Box>
                                ) )}
                            </Box>
                        ) )}
                    </Box>}


                    {activeSettingsTab === "audio" && <Box justifyContent='center' display="flex" alignItems="stretch" flexDirection="column" gap="30px" flexWrap="wrap" overflow="hidden" padding="20px">

                        <Box display="flex" flexDirection="column" maxWidth="100%">
                            <Typography variant='subtitle2' gutterBottom display="block" color='GrayText' fontSize="14px" >Microphone</Typography>
                            <Box display="flex" gap="30px" alignItems="center" position="relative" width="100%" borderRadius="5px" overflow="clip">
                                <Button disabled={!MeetingStore.getMeetingState?.micId} sx={{ borderRadius: "5px", display: "flex", justifyContent: "space-between", overflow: "hidden", maxWidth: "350px" }} onClick={e => setMicsAnchorEl( e.currentTarget )} endIcon={<ArrowDropDown fontSize='small' />} color="greyed" variant='outlined'>
                                    <Box fontSize="20px" minWidth={0} display="flex" alignItems="center" gap="10px" color={palette.common.font} >
                                        {Icons.default.Mic}
                                        <Typography color={!MeetingStore.getMeetingState?.micId ? "GrayText" : palette.common.font} variant='subtitle2' noWrap display="flex" gap="10px" alignItems="center" >   {!MeetingStore.getMeetingState?.micId ? "Permission required..." : MeetingStore.getIoDevices.mics.find( mic => mic.deviceId === MeetingStore.getMeetingState?.micId )?.label}</Typography>
                                    </Box>
                                </Button>
                                {MeetingStore.getMeetingState.isAudioOn ? <Box minWidth="30px" width="40px" sx={{ aspectRatio: "1/1" }} borderRadius="50%" bgcolor={palette.contentBg} display="flex" alignItems="center" justifyContent="center">
                                    <Box component="span" display="block" marginLeft="2px" >
                                        <AudioVisualizer size={20} audioTrack={MeetingStore.getLocalAudioTrack} />
                                    </Box>
                                </Box> : <Box minWidth="80px" display="flex" borderRadius="5px" padding="10px" bgcolor={palette.contentBg} justifyContent="center">
                                    <Typography textAlign="center" variant='subtitle2' fontSize="12px" >Mic is off!</Typography>
                                </Box>}
                            </Box>
                        </Box>
                        <Box display="flex" flexDirection="column" maxWidth="100%">
                            <Typography variant='subtitle2' gutterBottom display="block" color='GrayText' fontSize="14px" >Speaker</Typography>
                            <Box display="flex" gap="30px" alignItems="stretch" justifyContent="space-between" position="relative" width="100%" borderRadius="5px" overflow="clip">
                                <Button disabled={!MeetingStore.getMeetingState?.speakerId} sx={{ flexGrow: 1, borderRadius: "5px", display: "flex", justifyContent: "space-between", overflow: "hidden", maxWidth: "350px" }} onClick={e => setSpeakersAnchorEl( e.currentTarget )} endIcon={<ArrowDropDown fontSize='small' />} color="greyed" variant='outlined'>
                                    <Box fontSize="20px" minWidth={0} display="flex" alignItems="center" gap="10px" color={palette.common.font} >
                                        {Icons.default.VolumeUpIcon}
                                        <Typography overflow="hidden" minWidth={0} sx={{ textOverflow: "ellipsis" }} color={!MeetingStore.getMeetingState?.speakerId ? "GrayText" : palette.common.font} variant='subtitle2' noWrap gap="10px" width="100%" alignItems="center"  >  {!MeetingStore.getMeetingState?.speakerId ? "Permission required..." : MeetingStore.getIoDevices.speakers.find( speaker => speaker.deviceId === MeetingStore.getMeetingState?.speakerId )?.label}</Typography>
                                    </Box> </Button>
                                <Button onClick={togglePlaySampleAudio} sx={{ flexShrink: 0, minWidth: "80px", borderRadius: "20px", border: border[1], padding: "5px 20px" }} color='outlined' >
                                    {playingSampleAudio ? <DataLoadingDots padding='0' waitingMessage="Playing" /> : "Test"}
                                </Button>
                            </Box>
                        </Box>

                        <Menu disableAutoFocus disableEnforceFocus disableRestoreFocus onClose={() => setSpeakersAnchorEl( null )} anchorEl={speakersAnchorEl} transformOrigin={{ vertical: "top", horizontal: "left" }} anchorOrigin={{ vertical: "bottom", horizontal: "left" }} open={Boolean( speakersAnchorEl )}  >
                            {MeetingStore.getIoDevices.speakers.map( speaker => (
                                <MenuItem onClick={() => handleSpeakerChange( speaker.deviceId )} dense selected={speaker.deviceId === MeetingStore.getMeetingState?.speakerId} sx={{ fontSize: "14px" }} value={speaker.deviceId} key={speaker.deviceId}>{speaker.label}</MenuItem>
                            ) )}
                        </Menu>
                        <Menu disableAutoFocus disableEnforceFocus disableRestoreFocus onClose={() => setMicsAnchorEl( null )} anchorEl={micsAnchorEl} transformOrigin={{ vertical: "top", horizontal: "left" }} anchorOrigin={{ vertical: "bottom", horizontal: "left" }} open={Boolean( micsAnchorEl )}  >
                            {MeetingStore.getIoDevices.mics.map( mic => (
                                <MenuItem onClick={() => handleMicChange( mic.deviceId )} dense selected={mic.deviceId === MeetingStore.getMeetingState?.micId} sx={{ fontSize: "14px" }} value={mic.deviceId} key={mic.deviceId}>{mic.label}</MenuItem>
                            ) )}
                        </Menu>


                    </Box>}

                    {activeSettingsTab === "video" && <Box justifyContent='center' display="flex" alignItems="stretch" flexDirection="column" gap="10px" flexWrap="wrap" overflow="hidden" padding="20px">

                        {MeetingStore.getMeetingState.isVideoOn && <VideoPreview height='200px' styles={{ marginBottom: "20px" }} track={MeetingStore.getLocalVideoTrack} />}
                        {!MeetingStore.getMeetingState.isVideoOn && <Box height="180px" border={border[1]} borderRadius="20px" marginBottom="20px" bgcolor={palette.grey[900]} display="flex" flexDirection="column" alignItems="center" justifyContent="center" >
                            <Typography textAlign="center" color={palette.common.white} variant='h6' component="p" >Camera is turned off!</Typography>
                            <Typography textAlign="center" color={palette.common.white} variant="subtitle2" fontSize="12px">Turn on the camera to see results</Typography>
                        </Box>}
                        <Box width="100%">
                            <Typography variant='subtitle2' gutterBottom display="block" color='GrayText' fontSize="14px" >Camera</Typography>
                            <Box display="flex" gap="20px">
                                <Button fullWidth disabled={!MeetingStore.getMeetingState?.cameraId} sx={{ flexGrow: 1, borderRadius: "5px", justifyContent: "space-between" }} onClick={e => setCamsAnchorEl( e.currentTarget )} endIcon={<ArrowDropDown fontSize='small' />} color="greyed" variant='outlined'>
                                    <Typography color={!MeetingStore.getMeetingState?.cameraId ? "GrayText" : palette.common.font} variant='subtitle2' noWrap display="flex" gap="10px" alignItems="center" > <Videocam fontSize='small' /> {!MeetingStore.getMeetingState?.cameraId ? "Permission required..." : MeetingStore.getIoDevices.cameras.find( cam => cam.deviceId === MeetingStore.getMeetingState?.cameraId )?.label}</Typography>
                                </Button>
                            </Box>
                        </Box>

                        <Menu disableAutoFocus disableEnforceFocus disableRestoreFocus onClose={() => setCamsAnchorEl( null )} anchorEl={camsAnchorEl} transformOrigin={{ vertical: "top", horizontal: "left" }} anchorOrigin={{ vertical: "top", horizontal: "left" }} open={Boolean( camsAnchorEl )}  >
                            {MeetingStore.getIoDevices.cameras.map( cam => (
                                <MenuItem onClick={() => handleCameraChange( cam.deviceId )} dense selected={cam.deviceId === MeetingStore.getMeetingState?.cameraId} sx={{ fontSize: "14px" }} value={cam.deviceId} key={cam.deviceId}>{cam.label}</MenuItem>
                            ) )}
                        </Menu>


                    </Box>}
                </Box>
            </Box>
        </Dialog >
    )
}
)
export default MeetingSettings
